import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { Box, Collapse, Paper, Typography } from '@mui/material';
import React, { useState } from 'react';
import { formatUsdPerMonth, percentOf } from '../podDashboard.format';
import { POD_STATUS_COLORS, POD_STATUS_META, PodStatusSegment } from '../podDashboard.types';

interface Props {
    /** 1-based index shown before the label (e.g. "1 · Subscribed"). */
    index: number;
    segment: PodStatusSegment;
    totalPods: number;
    /** Optional drill-down content rendered when expanded. */
    children?: React.ReactNode;
    /** Start expanded. */
    defaultExpanded?: boolean;
}

/**
 * One status row in the dashboard list. Shows the colored accent, label,
 * description, an optional money figure, and pod count + share. Expands to
 * reveal `children` (the drill-down) when provided.
 */
const PodStatusSection = ({ index, segment, totalPods, children, defaultExpanded }: Props) => {
    const [expanded, setExpanded] = useState(Boolean(defaultExpanded));
    const meta = POD_STATUS_META[segment.key];
    const color = POD_STATUS_COLORS[segment.key];
    const share = percentOf(segment.podCount, totalPods);
    const expandable = Boolean(children);

    // Optional revenue line shown on the right (only when a figure is present).
    let moneyValue: string | null = null;
    let moneyLabel: string | null = null;
    if (segment.monthlyRevenue !== undefined && segment.monthlyRevenue !== null) {
        moneyValue = formatUsdPerMonth(segment.monthlyRevenue);
        moneyLabel = 'active monthly revenue (est.)';
    }

    return (
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Box
                role={expandable ? 'button' : undefined}
                tabIndex={expandable ? 0 : undefined}
                onClick={expandable ? () => setExpanded((v) => !v) : undefined}
                onKeyDown={
                    expandable
                        ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') setExpanded((v) => !v);
                          }
                        : undefined
                }
                sx={{
                    display: 'flex',
                    alignItems: 'stretch',
                    cursor: expandable ? 'pointer' : 'default',
                    '&:hover': expandable ? { backgroundColor: 'grey.50' } : undefined,
                }}
            >
                {/* Disclosure caret */}
                <Box sx={{ display: 'flex', alignItems: 'center', pl: 1.5 }}>
                    <ArrowRightIcon
                        sx={{
                            color: 'text.secondary',
                            transition: 'transform 150ms',
                            transform: expanded ? 'rotate(90deg)' : 'none',
                            visibility: expandable ? 'visible' : 'hidden',
                        }}
                    />
                </Box>

                {/* Colored accent bar */}
                <Box sx={{ width: 5, backgroundColor: color, my: 2, borderRadius: 1 }} />

                {/* Title + description */}
                <Box sx={{ flex: 1, p: 2, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>
                        {index} · {meta.label}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mt: 0.25 }}>
                        {meta.description}
                    </Typography>
                </Box>

                {/* Money figure */}
                {moneyValue && (
                    <Box sx={{ alignSelf: 'center', textAlign: 'right', px: 2 }}>
                        <Typography sx={{ fontWeight: 800 }}>{moneyValue}</Typography>
                        <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                            {moneyLabel}
                        </Typography>
                    </Box>
                )}

                {/* Pod count + share */}
                <Box
                    sx={{
                        alignSelf: 'center',
                        textAlign: 'right',
                        px: 2,
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 0.75,
                    }}
                >
                    <Typography sx={{ fontWeight: 800, fontSize: '1.4rem' }}>
                        {segment.podCount.toLocaleString('en-US')}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                        pods · {share}%
                    </Typography>
                </Box>
            </Box>

            {expandable && (
                <Collapse in={expanded} unmountOnExit>
                    <Box sx={{ px: 2, pb: 2 }}>{children}</Box>
                </Collapse>
            )}
        </Paper>
    );
};

export default PodStatusSection;
